// src/components/Dashboard.js
import React, { useState, useEffect } from 'react';
import { Outlet, useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import neo4jService from '../services/neo4jService';
import { clientQueries, departmentQueries } from '../repository/graphQueries';
import { authorize, logout } from '../services/accountService';
import { isAdminUser as isAdmin } from '../utils';
import { Menu, MenuItem } from "@headlessui/react";
import Logo from "./Logo";

const Dashboard = () => {
    const { clientId, staffId, departmentId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [neo4jConfigured, setNeo4jConfigured] = useState(false);
    const [allClientIds, setAllClientIds] = useState([]);
    const [allDepartmentIds, setAllDepartmentIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isAdminUser, setIsAdminUser] = useState(false)
    const [dateRange, setDateRange] = useState({ 
        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], 
        end: new Date().toISOString().split('T')[0] 
    });

    useEffect(() => {
        try {
            const auth = async () => {
                try {
                    const r = await authorize();
                    const {user} = r;
                    if (!user) {
                        navigate("/");
                        return
                    }
                    const admin = await isAdmin();
                    setIsAdminUser(() => admin);
                }  catch(e) {
                    console.log(e)
                    navigate("/");
                    return
                }
            }
            auth()
        } catch(e) {
            console.log(e)
            navigate("/");
            return
        }
    }, [navigate, neo4jConfigured])

    useEffect(() => {
        const connectAndFetchInitialData = async () => {
            if (neo4jService.driver && neo4jConfigured) {
                // If already configured, potentially re-fetch IDs if path changes type (client vs internal)
                // For simplicity now, we assume this is mainly for initial setup.
                // A more robust solution might refetch IDs if mainPathSegment changes significantly.
                setLoading(false); 
                return;
            }
            setLoading(true); setError(null); setNeo4jConfigured(false);
            try {
                const uri = process.env.REACT_APP_NEO4J_URI || 'neo4j://localhost:7687';
                const user = process.env.REACT_APP_NEO4J_USER || 'neo4j';
                const pass = process.env.REACT_APP_NEO4J_PASSWORD || 'password';
                const success = await neo4jService.initialize(uri, user, pass);

                if (success) {
                    setNeo4jConfigured(true);
                    // Fetch client IDs only if on a client-related path or default path
                    if (location.pathname.startsWith('/clients') || (!location.pathname.startsWith('/staff') && !location.pathname.startsWith('/internal'))) {
                        try {
                            const fetchedClients = await neo4jService.runQuery(clientQueries.listAllClients);
                            const clientIdsList = fetchedClients.map(r => r.clientId);
                            setAllClientIds(clientIdsList);
                            if (clientIdsList.length === 0 && location.pathname.startsWith('/clients') && !location.pathname.endsWith('/clients')) { // Don't error on /clients list view
                                 setError("No clients found in the database.");
                            }
                        } catch (clientFetchError) {
                            console.error("Failed to fetch client ID list:", clientFetchError);
                            setError("Failed to fetch client ID list.");
                        }
                    }
                    // Fetch department IDs only if on an internal path
                    if (location.pathname.startsWith('/internal')) {
                        try {
                            const fetchedDepts = await neo4jService.runQuery(departmentQueries.listAllDepartments);
                            const departmentIdsList = fetchedDepts.map(r => r.department).filter(d => d); // Filter out null/empty
                            setAllDepartmentIds(departmentIdsList);
                             if (departmentIdsList.length === 0 && location.pathname.startsWith('/internal') && !location.pathname.endsWith('/internal')) { // Don't error on /internal list view
                                 setError("No departments found. Ensure staff nodes have a 'department' property.");
                            }
                        } catch (deptFetchError) {
                            console.error("Failed to fetch department ID list:", deptFetchError);
                            setError("Failed to fetch department ID list.");
                        }
                    }
                } else {
                    setError('Failed to connect to Neo4j. Check credentials/URI.');
                }
            } catch (err) {
                setError(`Connection error: ${err.message}`);
            } finally {
                setLoading(false);
            }
        };
        connectAndFetchInitialData();
    }, [location.pathname]); // Re-run if path changes, to fetch correct IDs

    useEffect(() => {
        // Validate clientId only if we are on a client-specific path and have fetched client IDs
        if (clientId && neo4jConfigured && allClientIds.length > 0 && location.pathname.startsWith(`/clients/${clientId}`)) {
            const clientExists = allClientIds.some(id => id === clientId);
            if (!clientExists) {
                console.warn(`Client ID "${clientId}" not found. Redirecting to client list.`);
                navigate('/clients', { replace: true });
            }
        }
        // Validate departmentId only if we are on a department-specific path and have fetched department IDs
        if (departmentId && neo4jConfigured && allDepartmentIds.length > 0 && location.pathname.startsWith(`/internal/${departmentId}`)) {
            const departmentExists = allDepartmentIds.some(id => id === departmentId);
            if (!departmentExists) {
                console.warn(`Department ID "${departmentId}" not found. Redirecting to department list.`);
                navigate('/internal', { replace: true });
            }
        }
    }, [clientId, departmentId, neo4jConfigured, allClientIds, allDepartmentIds, navigate, location.pathname]);


    const handleDateRangeChange = (key, value) => { setDateRange(prev => ({ ...prev, [key]: value })); };

    // Header and Sidebar Logic (remains mostly the same, but with the new Issues tab)
    const pathSegments = location.pathname.split('/').filter(p => p);
    const mainPathSegment = pathSegments.length > 0 ? pathSegments[0] : 'clients'; 

    let currentSubView = '';
    if (mainPathSegment === 'clients' && clientId && pathSegments.length > 2) {
        currentSubView = pathSegments[2];
    } else if (mainPathSegment === 'internal' && departmentId && pathSegments.length > 2) {
        currentSubView = pathSegments[2];
    }


    const getHeaderTitle = () => {
        if (mainPathSegment === 'clients') {
            return `Client Success ${clientId ? `> ${allClientIds.find(c => c === clientId) || clientId}` : ''}`;
        }
        if (mainPathSegment === 'staff') {
            return `Staff View ${staffId ? `> ${staffId}` : ''}`; // Assuming staff details might include a name
        }
        if (mainPathSegment === 'internal') {
            return `Internal View ${departmentId ? `> ${allDepartmentIds.find(d => d === departmentId) || departmentId}` : ''}`;
        }
        return 'Dashboard';
    };
    
    const clientSpecificNavItems = [
        { view: 'overview', label: 'Overview', path: `/clients/${clientId}/overview`, icon: <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" /> },
        { view: 'goals', label: 'Goals & Initiatives', path: `/clients/${clientId}/goals`, icon: <path fillRule="evenodd" d="M.2 10a11.9 11.9 0 0 1 19.6 0A11.9 11.9 0 0 1 .2 10zm10.36 4.36a1 1 0 0 0-1.12 0l-2.5 2.5a1 1 0 1 0 1.41 1.41l2.5-2.5a1 1 0 0 0-.79-1.41zM6.17 5.17a1 1 0 1 0-1.41-1.41l-2.5 2.5a1 1 0 0 0 1.41 1.41l2.5-2.5zm8.05 0a1 1 0 1 0-1.41 1.41l2.5 2.5a1 1 0 0 0 1.41-1.41l-2.5-2.5zM13 10a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" clipRule="evenodd" /> },
        { view: 'issues', label: 'Issues', path: `/clients/${clientId}/issues`, icon: <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /> },
        { view: 'action-items', label: 'Action Items', path: `/clients/${clientId}/action-items`, icon: <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /> },
        { view: 'sentiment', label: 'Comms & Sentiment', path: `/clients/${clientId}/sentiment`, icon: <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 3a1 1 0 100 2h2a1 1 0 100-2H9z" clipRule="evenodd" /> },
        { type: 'divider' },
        { view: 'graph', label: 'Knowledge Graph', path: `/clients/${clientId}/graph`, icon: <path fillRule="evenodd" d="M5 3a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V5a2 2 0 00-2-2H5zm9 4a1 1 0 10-2 0v6a1 1 0 102 0V7zm-3 2a1 1 0 10-2 0v4a1 1 0 102 0V9zm-3 3a1 1 0 10-2 0v1a1 1 0 102 0v-1z" clipRule="evenodd" /> },
        { view: 'insights', label: 'AI Insights', path: `/clients/${clientId}/insights`, icon: <path fillRule="evenodd" d="M11 3a1 1 0 100 2h.01a1 1 0 100-2H11zM5 3a1 1 0 00-1 1v.01a1 1 0 001 1H6a1 1 0 001-1V4a1 1 0 00-1-1H5zM3 11a1 1 0 100 2h.01a1 1 0 100-2H3zM3 17a1 1 0 100 2h.01a1 1 0 100-2H3zM9 17a1 1 0 100 2h2a1 1 0 100-2H9zM11 13a1 1 0 100 2h.01a1 1 0 100-2H11zM15 3a1 1 0 100 2h.01a1 1 0 100-2H15zM15 9a1 1 0 100 2h.01a1 1 0 100-2H15zM15 15a1 1 0 100 2h.01a1 1 0 100-2H15z" clipRule="evenodd" /> },
    ];
    
    const departmentSpecificNavItems = [
        { view: 'overview', label: 'Overview', path: `/internal/${departmentId}/overview`, icon: <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" /> },
        { view: 'goals', label: 'Goals & Initiatives', path: `/internal/${departmentId}/goals`, icon: <path fillRule="evenodd" d="M.2 10a11.9 11.9 0 0 1 19.6 0A11.9 11.9 0 0 1 .2 10zm10.36 4.36a1 1 0 0 0-1.12 0l-2.5 2.5a1 1 0 1 0 1.41 1.41l2.5-2.5a1 1 0 0 0-.79-1.41zM6.17 5.17a1 1 0 1 0-1.41-1.41l-2.5 2.5a1 1 0 0 0 1.41 1.41l2.5-2.5zm8.05 0a1 1 0 1 0-1.41 1.41l2.5 2.5a1 1 0 0 0 1.41-1.41l-2.5-2.5zM13 10a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" clipRule="evenodd" /> },
        { view: 'action-items', label: 'Action Items', path: `/internal/${departmentId}/action-items`, icon: <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /> },
        { view: 'issues', label: 'Issues', path: `/internal/${departmentId}/issues`, icon: <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /> }, // Added Issues tab
        { view: 'sentiment', label: 'Comms & Sentiment', path: `/internal/${departmentId}/sentiment`, icon: <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 3a1 1 0 100 2h2a1 1 0 100-2H9z" clipRule="evenodd" /> },
    ];

     if (loading && !neo4jConfigured) { return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-800"></div><span className="ml-3">Connecting to Database...</span></div>;}
    if (!neo4jConfigured && error && !loading) { return <div className="p-8 bg-red-100 text-red-800 flex flex-col items-center justify-center h-screen"><h2>Connection Error</h2><p>{error}</p><button onClick={() => window.location.reload()} className="mt-4 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition duration-200">Retry Connection</button></div>;}
    if (loading) { return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-800"></div><span className="ml-3">Setting up Dashboard...</span></div>;}

    function MenuItemWrapper({ children, onClick }) {
        return (
            <MenuItem
            as="button"
            onClick={onClick}
            className="w-full text-left px-4 py-2 text-sm ui-active:bg-gray-100 hover:text-gray-500"
            >
            {children}
            </MenuItem>
        );
    }

    async function userLogout() {
        try {
          await logout();
          navigate("/");
        }
        catch(e) {
          console.log(e)
          return;
        }
    }

    return (
        <div className="min-h-screen bg-gray-100 flex flex-col">
            <header className="bg-white shadow-sm sticky top-0 z-10">
                <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-3 flex justify-between items-center">
                    <div className="flex items-center space-x-3 min-w-0">
                        <Logo size={32} className="h-8 w-8" />
                        <h1 className="text-xl md:text-2xl font-bold text-blue-800 truncate">
                            {getHeaderTitle()}
                        </h1>
                    </div>
                     <div className="flex space-x-2 sm:space-x-4 items-center">
                        <div className="hidden md:flex space-x-2 items-center">
                            <label htmlFor="startDate" className="text-sm text-gray-700">From:</label>
                            <input id="startDate" type="date" className="px-2 py-1 border border-gray-300 rounded-md text-sm" value={dateRange.start} onChange={(e) => handleDateRangeChange('start', e.target.value)} />
                            <label htmlFor="endDate" className="text-sm text-gray-700">To:</label>
                            <input id="endDate" type="date" className="px-2 py-1 border border-gray-300 rounded-md text-sm" value={dateRange.end} onChange={(e) => handleDateRangeChange('end', e.target.value)} />
                        </div>
                        <div className="relative hidden sm:block">
                            <input type="text" placeholder="Search..." className="pl-8 pr-3 py-1 md:py-2 border border-gray-300 rounded-md text-sm md:text-base" />
                            <svg className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" /></svg>
                        </div>

                        {/* User Dropdown */}
                        <Menu as="div" className="relative">
                            <Menu.Button className="flex items-center space-x-1 outline-none">
                                <svg className="w-8 h-8 text-gray-800 dark:text-dark" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                                <path fillRule="evenodd" d="M12 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm-2 9a4 4 0 0 0-4 4v1a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-1a4 4 0 0 0-4-4h-4Z" clipRule="evenodd"/>
                                </svg>
                            </Menu.Button>
                            <Menu.Items className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-md shadow-lg py-1 z-50  outline-none">
                                <MenuItemWrapper onClick={() => window.open("/update-password", "_blank")}>
                                    Update Password
                                </MenuItemWrapper>
                                <MenuItemWrapper onClick={() => userLogout()}>
                                    Logout
                                </MenuItemWrapper>
                            </Menu.Items>
                        </Menu>
                        
                    </div>
                </div>
            </header>

            {error && !loading && ( <div className="w-full px-4 sm:px-6 lg:px-8 py-2"><div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-3 rounded"><p className="font-bold">Error:</p><p>{error}</p></div></div> )}
            
            {/* Add mobile date pickers here if desired, conditionally rendered */}
            <div className="md:hidden px-4 pt-2 pb-1 bg-gray-50 border-b border-gray-200 flex flex-col space-y-2">
                <div className="flex items-center justify-between">
                    <label htmlFor="startDateMobile" className="text-xs text-gray-600">From:</label>
                    <input id="startDateMobile" type="date" className="px-2 py-1 border border-gray-300 rounded-md text-xs w-36" value={dateRange.start} onChange={(e) => handleDateRangeChange('start', e.target.value)} />
                </div>
                <div className="flex items-center justify-between">
                     <label htmlFor="endDateMobile" className="text-xs text-gray-600">To:</label>
                    <input id="endDateMobile" type="date" className="px-2 py-1 border border-gray-300 rounded-md text-xs w-36" value={dateRange.end} onChange={(e) => handleDateRangeChange('end', e.target.value)} />
                </div>
            </div>


            <div className="flex-1 flex mt-[56px]"> {/* Adjust mt based on actual header height */}
                <aside className="w-56 md:w-64 bg-white shadow-md overflow-y-auto fixed h-[calc(100vh-56px)] top-[56px]"> {/* Adjust top based on header height */}
                    <nav className="px-2 md:px-4 py-4">
                        <ul className="space-y-1 md:space-y-2">
                            <li>
                                <Link
                                    to="/clients"
                                    title="Client View"
                                    className={`w-full text-left px-2 py-2 md:px-4 md:py-2 rounded-md flex items-center text-sm md:text-base ${mainPathSegment === 'clients' && !clientId && !departmentId ? 'bg-blue-100 text-blue-800 font-semibold' : 'text-gray-700 hover:bg-gray-100'}`}
                                >
                                    <svg className="mr-3 h-5 w-5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" /></svg>
                                    <span className="truncate">Client View</span>
                                </Link>
                            </li>
                            <li>
                                <Link
                                    to="/staff"
                                    title="Staff View"
                                    className={`w-full text-left px-2 py-2 md:px-4 md:py-2 rounded-md flex items-center text-sm md:text-base ${mainPathSegment === 'staff' ? 'bg-blue-100 text-blue-800 font-semibold' : 'text-gray-700 hover:bg-gray-100'}`}
                                >
                                     <svg className="mr-3 h-5 w-5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 2a1 1 0 00-1 1v1a1 1 0 002 0V3a1 1 0 00-1-1zM4 4a1 1 0 00-1 1v1a1 1 0 002 0V5a1 1 0 00-1-1zM4 9a1 1 0 00-1 1v1a1 1 0 002 0v-1a1 1 0 00-1-1zM4 14a1 1 0 00-1 1v1a1 1 0 002 0v-1a1 1 0 00-1-1zM9 4a1 1 0 00-1 1v1a1 1 0 002 0V5a1 1 0 00-1-1zM9 9a1 1 0 00-1 1v1a1 1 0 002 0v-1a1 1 0 00-1-1zM9 14a1 1 0 00-1 1v1a1 1 0 002 0v-1a1 1 0 00-1-1zM14 4a1 1 0 00-1 1v1a1 1 0 002 0V5a1 1 0 00-1-1zM14 9a1 1 0 00-1 1v1a1 1 0 002 0v-1a1 1 0 00-1-1zM14 14a1 1 0 00-1 1v1a1 1 0 002 0v-1a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                                    <span className="truncate">Staff View</span>
                                </Link>
                            </li>
                            {isAdminUser ? <li>
                                <Link
                                    to="/internal"
                                    title="Internal View"
                                    className={`w-full text-left px-2 py-2 md:px-4 md:py-2 rounded-md flex items-center text-sm md:text-base ${mainPathSegment === 'internal' && !departmentId ? 'bg-blue-100 text-blue-800 font-semibold' : 'text-gray-700 hover:bg-gray-100'}`}
                                >
                                   <svg className="mr-3 h-5 w-5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                    </svg>
                                    <span className="truncate">Internal View</span>
                                </Link>
                            </li> : null}
                            

                            {clientId && mainPathSegment === 'clients' && (
                                <>
                                    <hr className="my-3 md:my-4" />
                                    <li className="px-2 md:px-4 pt-2 pb-1 text-xs text-gray-500 uppercase tracking-wider">
                                        Client: {allClientIds.find(c => c === clientId) || clientId}
                                    </li>
                                    {clientSpecificNavItems.map((item, index) => {
                                        if (item.type === 'divider') return <hr className="my-2 md:my-4" key={`client-divider-${index}`}/>;
                                        
                                        let isActive = currentSubView === item.view;
                                        if (item.view === 'overview' && (!currentSubView || currentSubView === 'overview')) { 
                                            isActive = true;
                                        }

                                        return (
                                            <li key={item.view || `client-item-${index}`}>
                                                <Link
                                                    to={item.path}
                                                    title={item.label}
                                                    className={`w-full text-left px-2 py-2 md:px-4 md:py-2 rounded-md flex items-center text-sm md:text-base ${isActive ? 'bg-blue-100 text-blue-800 font-semibold' : 'text-gray-700 hover:bg-gray-100'}`}
                                                >
                                                    <svg className="mr-3 h-5 w-5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">{item.icon}</svg>
                                                    <span className="truncate">{item.label}</span>
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </>
                            )}
                            {departmentId && mainPathSegment === 'internal' && (
                                <>
                                    <hr className="my-3 md:my-4" />
                                    <li className="px-2 md:px-4 pt-2 pb-1 text-xs text-gray-500 uppercase tracking-wider">
                                        Dept: {allDepartmentIds.find(d => d === departmentId) || departmentId}
                                    </li>
                                    {departmentSpecificNavItems.map((item, index) => {
                                        if (item.type === 'divider') return <hr className="my-2 md:my-4" key={`dept-divider-${index}`}/>;
                                        
                                        let isActive = currentSubView === item.view;
                                        if (item.view === 'overview' && (!currentSubView || currentSubView === 'overview')) {
                                            isActive = true;
                                        }

                                        return (
                                            <li key={item.view || `dept-item-${index}`}>
                                                <Link
                                                    to={item.path}
                                                    title={item.label}
                                                    className={`w-full text-left px-2 py-2 md:px-4 md:py-2 rounded-md flex items-center text-sm md:text-base ${isActive ? 'bg-blue-100 text-blue-800 font-semibold' : 'text-gray-700 hover:bg-gray-100'}`}
                                                >
                                                     <svg className="mr-3 h-5 w-5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">{item.icon}</svg>
                                                    <span className="truncate">{item.label}</span>
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </>
                            )}
                        </ul>
                    </nav>
                </aside>

                <main className="flex-1 overflow-y-auto p-4 md:p-8 ml-56 md:ml-64"> {/* Adjust ml if sidebar width changes */}
                    {neo4jConfigured ? (
                        <Outlet context={{ neo4jService, clientId, staffId, departmentId, dateRange, allClientIds, allDepartmentIds }} />
                    ) : (
                        <div className="flex items-center justify-center h-64">
                           <p>Waiting for Neo4j connection...</p>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

export default Dashboard;